/**
 * Budget Utility Functions
 *
 * Pure helper functions shared across budget-card, budget-detail, and other
 * budget components. Extracted to eliminate duplication.
 */

// ---------------------------------------------------------------------------
// Date calculations
// ---------------------------------------------------------------------------

/** Number of calendar days between two ISO date strings (inclusive). */
export function daysBetween(start: string, end: string): number {
  const s = new Date(start);
  const e = new Date(end);
  return Math.max(1, Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1);
}

/** Number of calendar days elapsed since period start (inclusive of today). */
export function daysElapsedInPeriod(periodStart: string): number {
  const start = new Date(periodStart);
  const now = new Date();
  return Math.max(1, Math.round((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
}

/** Number of calendar days remaining until period end. */
export function daysLeftInPeriod(periodEnd: string): number {
  const end = new Date(periodEnd);
  const now = new Date();
  const diff = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** Short label for the current period: "week" | "month" | "year" */
export function periodLabel(periodType: string): string {
  if (periodType === 'weekly') return 'week';
  if (periodType === 'yearly') return 'year';
  return 'month';
}

/** Label for the previous period: "Last Week" | "Last Month" | "Last Year" */
export function previousPeriodLabel(periodType: string): string {
  if (periodType === 'weekly') return 'Last Week';
  if (periodType === 'yearly') return 'Last Year';
  return 'Last Month';
}

/** Display label for the period type: "Weekly" | "Monthly" | "Yearly" */
export function formatPeriodType(periodType: string): string {
  const labels: Record<string, string> = { weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' };
  return labels[periodType] ?? periodType;
}

// ---------------------------------------------------------------------------
// Progress colors (semantic tokens — theme-aware)
// ---------------------------------------------------------------------------

/** Background color class for a budget progress bar. */
export function getProgressColor(percentUsed: number, notifyAtPercent: number): string {
  if (percentUsed >= 100) return 'bg-destructive';
  if (percentUsed >= notifyAtPercent) return 'bg-accent';
  return 'bg-success';
}

/** Text color class for budget status text. */
export function getTextColor(percentUsed: number, notifyAtPercent: number): string {
  if (percentUsed >= 100) return 'text-destructive';
  if (percentUsed >= notifyAtPercent) return 'text-accent';
  return 'text-success';
}
